import type { SchemaInput, SchemaRecord } from './schema-input.js';
import { isObject, isString } from './runtime-values.js';
export type ServiceAreaGeometry =
  | { type: 'Polygon'; coordinates: number[][][] }
  | { type: 'MultiPolygon'; coordinates: number[][][][] };

export type ServiceAreaMapVisibility = 'after-check' | 'always' | 'hidden';
export type ServiceAreaWidgetLayout = 'overlay' | 'split';

export interface ServiceAreaConfig {
  version: 1;
  name: string;
  geometry: ServiceAreaGeometry;
  display: {
    showBoundary: boolean;
    themeColor: string;
    mapVisibility: ServiceAreaMapVisibility;
    layout: ServiceAreaWidgetLayout;
  };
  widget: {
    businessName: string;
    showBusinessName: boolean;
    title: string;
    description: string;
    placeholder: string;
    insideMessage: string;
    outsideMessage: string;
    ctaLabel: string;
    ctaHref: string;
    outsideCtaLabel: string;
    outsideCtaHref: string;
    enableGeolocation: boolean;
  };
}

export const DEFAULT_SERVICE_AREA_WIDGET = {
  businessName: '',
  showBusinessName: false,
  title: 'Do we serve your address?',
  description: 'Enter your address to see whether service is available.',
  placeholder: 'Street address, city or postcode',
  insideMessage: 'Yes, we serve this address.',
  outsideMessage: 'This address is outside our standard service area.',
  ctaLabel: 'Request a quote',
  ctaHref: '',
  outsideCtaLabel: 'Contact us anyway',
  outsideCtaHref: '',
  enableGeolocation: true,
} satisfies ServiceAreaConfig['widget'];

export const DEFAULT_SERVICE_AREA_THEME_COLOR = '#2563eb';
export const MAX_SERVICE_AREA_GEOMETRY_POINTS = 50_000;
const MAX_SERVICE_AREA_GEOMETRY_RINGS = 1_000;
const MAX_SERVICE_AREA_GEOMETRY_POLYGONS = 1_000;

function normalizedWidgetText(
  value: SchemaInput,
  fallback: string,
  maxLength: number,
): string {
  if (!isString(value) || !value.trim()) return fallback;
  return value.slice(0, maxLength);
}

function cloneGeometry(geometry: ServiceAreaGeometry): ServiceAreaGeometry {
  if (geometry.type === 'Polygon') {
    return { type: 'Polygon', coordinates: geometry.coordinates.map((ring) => ring.map((p) => [p[0], p[1]])) };
  }
  return {
    type: 'MultiPolygon',
    coordinates: geometry.coordinates.map((poly) => poly.map((ring) => ring.map((p) => [p[0], p[1]]))),
  };
}

function isValidLngLatPair(value: SchemaInput): value is [number, number] {
  return Array.isArray(value)
    && value.length >= 2
    && Number.isFinite(value[0])
    && Number.isFinite(value[1])
    && Math.abs(value[0]) <= 180
    && Math.abs(value[1]) <= 90;
}

export function closeServiceAreaRing(ring: number[][]): number[][] {
  if (ring.length === 0) return [];
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) return ring;
  return [...ring, [first[0], first[1]]];
}

export function serviceAreaRingArea(ring: number[][]): number {
  let area = 0;
  for (let i = 0; i < ring.length - 1; i += 1) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[i + 1];
    area += x1 * y2 - x2 * y1;
  }
  return area / 2;
}

export function normalizeServiceAreaConfig(value: SchemaInput): ServiceAreaConfig | null {
  if (!value || !isObject(value)) return null;
  // SAFETY: The adjacent object and field checks validate this boundary value before it is accepted.
  const input = value as Partial<ServiceAreaConfig>;
  if (input.version !== 1 || !input.geometry || !isObject(input.geometry)) return null;

  let geometry: ServiceAreaGeometry | null = null;
  if (input.geometry.type === 'Polygon' && Array.isArray(input.geometry.coordinates)) {
    if (input.geometry.coordinates.length > MAX_SERVICE_AREA_GEOMETRY_RINGS) return null;
    let rawPointCount = 0;
    for (const ring of input.geometry.coordinates) {
      if (Array.isArray(ring)) rawPointCount += ring.length;
      if (rawPointCount > MAX_SERVICE_AREA_GEOMETRY_POINTS) return null;
    }
    const rings = input.geometry.coordinates
      .filter((ring): ring is number[][] => Array.isArray(ring))
      .map((ring) => closeServiceAreaRing(ring.filter(isValidLngLatPair).map((p) => [p[0], p[1]])))
      .filter((ring) => ring.length >= 4 && Math.abs(serviceAreaRingArea(ring)) > 0.0000000001);
    if (rings.length > 0) geometry = { type: 'Polygon', coordinates: rings };
  } else if (input.geometry.type === 'MultiPolygon' && Array.isArray(input.geometry.coordinates)) {
    if (input.geometry.coordinates.length > MAX_SERVICE_AREA_GEOMETRY_POLYGONS) return null;
    let rawRingCount = 0;
    let rawPointCount = 0;
    for (const polygon of input.geometry.coordinates) {
      if (!Array.isArray(polygon)) continue;
      rawRingCount += polygon.length;
      if (rawRingCount > MAX_SERVICE_AREA_GEOMETRY_RINGS) return null;
      for (const ring of polygon) {
        if (Array.isArray(ring)) rawPointCount += ring.length;
        if (rawPointCount > MAX_SERVICE_AREA_GEOMETRY_POINTS) return null;
      }
    }
    const polygons = input.geometry.coordinates
      .filter((poly): poly is number[][][] => Array.isArray(poly))
      .map((poly) => poly
        .filter((ring): ring is number[][] => Array.isArray(ring))
        .map((ring) => closeServiceAreaRing(ring.filter(isValidLngLatPair).map((p) => [p[0], p[1]])))
        .filter((ring) => ring.length >= 4 && Math.abs(serviceAreaRingArea(ring)) > 0.0000000001))
      .filter((poly) => poly.length > 0);
    if (polygons.length > 0) geometry = { type: 'MultiPolygon', coordinates: polygons };
  }
  if (!geometry) return null;

  const widget = input.widget && isObject(input.widget)
    // SAFETY: The adjacent object and field checks validate this boundary value before it is accepted.
    ? /* SAFETY: The adjacent boundary checks validate every field that this code reads. */ input.widget as SchemaRecord
    : {};
  const display = input.display && isObject(input.display)
    // SAFETY: The adjacent object and field checks validate this boundary value before it is accepted.
    ? /* SAFETY: The adjacent boundary checks validate every field that this code reads. */ input.display as SchemaRecord
    : {};
  const themeColor = isString(display.themeColor) && /^#[0-9a-fA-F]{6}$/.test(display.themeColor)
    ? display.themeColor
    : DEFAULT_SERVICE_AREA_THEME_COLOR;
  const mapVisibility = display.mapVisibility === 'after-check'
    || display.mapVisibility === 'always'
    || display.mapVisibility === 'hidden'
    ? display.mapVisibility
    : 'always';
  const layout = display.layout === 'split' ? 'split' : 'overlay';

  return {
    version: 1,
    name: isString(input.name) && input.name.trim() ? input.name.trim().slice(0, 80) : 'Service area',
    geometry,
    display: {
      showBoundary: display.showBoundary !== false,
      themeColor,
      mapVisibility,
      layout,
    },
    widget: {
      businessName: isString(widget.businessName) ? widget.businessName.slice(0, 120) : DEFAULT_SERVICE_AREA_WIDGET.businessName,
      showBusinessName: widget.showBusinessName === true,
      title: normalizedWidgetText(widget.title, DEFAULT_SERVICE_AREA_WIDGET.title, 140),
      description: normalizedWidgetText(widget.description, DEFAULT_SERVICE_AREA_WIDGET.description, 200),
      placeholder: normalizedWidgetText(widget.placeholder, DEFAULT_SERVICE_AREA_WIDGET.placeholder, 140),
      insideMessage: normalizedWidgetText(widget.insideMessage, DEFAULT_SERVICE_AREA_WIDGET.insideMessage, 240),
      outsideMessage: normalizedWidgetText(widget.outsideMessage, DEFAULT_SERVICE_AREA_WIDGET.outsideMessage, 240),
      ctaLabel: isString(widget.ctaLabel) && widget.ctaLabel.trim() ? widget.ctaLabel.slice(0, 80) : DEFAULT_SERVICE_AREA_WIDGET.ctaLabel,
      ctaHref: isString(widget.ctaHref) ? widget.ctaHref.slice(0, 300) : DEFAULT_SERVICE_AREA_WIDGET.ctaHref,
      outsideCtaLabel: isString(widget.outsideCtaLabel) && widget.outsideCtaLabel.trim() ? widget.outsideCtaLabel.slice(0, 80) : DEFAULT_SERVICE_AREA_WIDGET.outsideCtaLabel,
      outsideCtaHref: isString(widget.outsideCtaHref) ? widget.outsideCtaHref.slice(0, 300) : DEFAULT_SERVICE_AREA_WIDGET.outsideCtaHref,
      enableGeolocation: widget.enableGeolocation === true,
    },
  };
}

export function cloneServiceAreaConfig(config: ServiceAreaConfig): ServiceAreaConfig {
  return {
    version: 1,
    name: config.name,
    geometry: cloneGeometry(config.geometry),
    display: { ...config.display },
    widget: { ...config.widget },
  };
}
